import { foldedRanges, foldEffect, unfoldEffect } from "@codemirror/language";
import { EditorState, RangeSetBuilder, StateField } from "@codemirror/state";
import {
    Decoration,
    type DecorationSet,
    EditorView,
} from "@codemirror/view";
import { collectMarkdownMathExcludedLines } from "../build-decorations";
import { collectMarkdownMathBlockRanges } from "../markdown-math/scan";
import { magicScanField } from "../scan-state";
import {
    forceL2Field,
    magicSyntaxFocusedField,
    setForceL2Mode,
    setMagicSyntaxFocused,
} from "../state-fields";
import { collectMarkdownRichBlockRanges } from "./scan";
import { PrettyMarkdownRichBlockWidget } from "./widgets";

function selectionTouchesLineRange(
    state: EditorState,
    startLine: number,
    endLine: number,
): boolean {
    for (const range of state.selection.ranges) {
        const headLine = state.doc.lineAt(range.head).number;
        if (headLine >= startLine && headLine <= endLine) return true;
        if (!range.empty) {
            const anchorLine = state.doc.lineAt(range.anchor).number;
            if (anchorLine >= startLine && anchorLine <= endLine) return true;
        }
    }
    return false;
}

function hasFoldedRange(
    state: EditorState,
    from: number,
    to: number,
): boolean {
    const folded = foldedRanges(state).iter();
    while (folded.value) {
        if (folded.from === from && folded.to === to) return true;
        folded.next();
    }
    return false;
}

function buildMarkdownRichBlockDecorations(
    state: EditorState,
): DecorationSet {
    const scan = state.field(magicScanField).scan;
    const forceL2 = state.field(forceL2Field);
    const hasFocus = state.field(magicSyntaxFocusedField);
    if (forceL2) return Decoration.none;

    const markdownMathExcludedLines = collectMarkdownMathExcludedLines(scan);
    const markdownMathBlockRanges = collectMarkdownMathBlockRanges(
        state.doc,
        markdownMathExcludedLines,
    );
    const occupiedLines = new Set<number>();
    for (const range of markdownMathBlockRanges) {
        for (let lineNumber = range.startLine; lineNumber <= range.endLine; lineNumber += 1) {
            occupiedLines.add(lineNumber);
        }
    }

    const ranges = collectMarkdownRichBlockRanges(
        state.doc,
        markdownMathExcludedLines,
        occupiedLines,
    );
    if (ranges.length === 0) return Decoration.none;

    const builder = new RangeSetBuilder<Decoration>();
    for (const range of ranges) {
        if (
            hasFocus &&
            selectionTouchesLineRange(state, range.startLine, range.endLine)
        ) {
            continue;
        }

        const firstLine = state.doc.line(range.startLine);
        const lastLine = state.doc.line(range.endLine);
        const from = firstLine.from;
        const editPosition = firstLine.from + range.fromIndex;
        if (
            range.startLine !== range.endLine &&
            !hasFoldedRange(state, firstLine.to, lastLine.to)
        ) {
            continue;
        }

        builder.add(
            from,
            firstLine.to,
            Decoration.replace({
                widget: new PrettyMarkdownRichBlockWidget(range, editPosition),
                block: false,
            }),
        );
    }

    return builder.finish();
}

export const markdownRichBlockField = StateField.define<DecorationSet>({
    create: (state) => buildMarkdownRichBlockDecorations(state),
    update(value, tr) {
        const focusChanged = tr.effects.some((effect) =>
            effect.is(setMagicSyntaxFocused)
        );
        const forceL2Changed = tr.effects.some((effect) =>
            effect.is(setForceL2Mode)
        );
        const foldChanged = tr.effects.some((effect) =>
            effect.is(foldEffect) || effect.is(unfoldEffect)
        );
        if (!tr.docChanged && !tr.selection && !focusChanged && !forceL2Changed && !foldChanged) {
            return value;
        }
        return buildMarkdownRichBlockDecorations(tr.state);
    },
    provide: (field) => EditorView.decorations.from(field),
});

export const markdownRichFocusSync = EditorView.domEventHandlers({
    focus(_event, view) {
        view.dispatch({ effects: setMagicSyntaxFocused.of(true) });
        return false;
    },
    blur(_event, view) {
        view.dispatch({ effects: setMagicSyntaxFocused.of(false) });
        return false;
    },
});
